import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { createEventEmitter, VueEventEmitterFacade } from "utils/eventEmitter";
import { App } from "vue";
import { EncounterDto } from "./api/entities/encounters";
import { getActiveUserManager } from "./store/userManagers";
import { useTaskStore } from "./store/tasks";
import { useEncounterStore } from "./store/encounters";
import { TaskDto } from "./api/entities/tasks";

type TaskEvents = {
  "connected": boolean;
  "tasknote-updated-MEMBERTASK": TaskDto;
  "tasknote-done-MEMBERTASK": TaskDto;
  "tasknote-deleted-MEMBERTASK": TaskDto;
};

type EncounterEvents = {
  connected: boolean;
  encounterUpdated: EncounterDto | null;
};

export interface taskNotesHubType extends VueEventEmitterFacade<TaskEvents> {
  start(): Promise<void>;
  connected(): boolean;
  stop(): void;
  watchMyEzra(): Promise<TaskDto[]>;
  unwatchMyEzra(): Promise<boolean>;
  complete(taskId: string, taskType: "MEMBERTASK"): Promise<boolean>;
}

export interface encountersHubType
  extends VueEventEmitterFacade<EncounterEvents> {
  start(): Promise<void>;
  connected(): boolean;
  stop(): void;
  watch(): Promise<EncounterDto[]>;
  unwatch(): Promise<boolean>;
}

let installedEncountersHub: encountersHubType | null = null;

/** Hub instance after app install; used outside Vue components (e.g. MQ flowData). */
export function getEncountersHub(): encountersHubType | null {
  return installedEncountersHub;
}

export const tasksHub = {
  install(app: App) {
    const connection = new HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_APP_TASKNOTES_URL}/api/tasknoteshub`, {
        accessTokenFactory: async () => {
          const user = await (await getActiveUserManager())?.getUser();
          if (user && !user.expired) {
            return user.access_token;
          } else {
            return "";
          }
        }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Error)
      .build();

    let startedPromise: Promise<void> = Promise.resolve();
    function start() {
      if (connection.state == "Connected") {
        console.warn("Hub connection already established.");
        return Promise.resolve();
      }
      startedPromise = connection.start().catch(async () => {
        const user = await (await getActiveUserManager())?.getUser();
        if (!user || user.expired) {
          console.warn("Hub connection unavailable.");
          return Promise.resolve();
        }
        return new Promise((resolve, reject) =>
          setTimeout(() => start().then(resolve).catch(reject), 5000)
        );
      });
      return startedPromise;
    }

    const taskNotesEmitter = createEventEmitter<TaskEvents>();
    const taskStore = useTaskStore();
    const taskNotesHub = {
      ...taskNotesEmitter,
      start: async () => {
        await start();
        taskNotesHub.$emit("connected", true);
      },
      connected: () => connection?.state === "Connected",
      stop: () => {
        if (taskNotesHub.connected()) {
          connection.stop().catch(err => {
            console.error("Failed to disconnect with hub", err);
          });
        } else {
          console.warn(
            "Attempted to stop listening to hub, but was not connected."
          );
        }
      },
      watchMyEzra: () => {
        if (!startedPromise) {
          console.error("Hub has no connection.");
          return Promise.reject();
        }
        return startedPromise
          .then(() => {
            const promise = connection.invoke("watchMyEzra");
            return promise;
          })
          .then(val => val && taskStore.setState(val))
          .catch(e => {
            console.error(e);
            return [];
          });
      },
      unwatchMyEzra: () => {
        if (!startedPromise) {
          console.error("Hub has no connection.");
          return Promise.reject();
        }
        return startedPromise
          .then(() => connection.invoke("unwatchMyEzra"))
          .then(() => {
            taskStore.$reset();
            return true;
          })
          .catch(e => {
            console.error(e);
            return false;
          });
      },
      complete: (taskId: string) => {
        if (!startedPromise) {
          console.error("Hub has no connection.");
          return Promise.reject();
        }
        return startedPromise.then(() =>
          connection.invoke("myezraComplete", taskId, "MEMBERTASK")
        );
      }
    };

    app.config.globalProperties.$taskNotesHub = taskNotesHub;

    connection.onreconnecting(() => {
      taskNotesHub.$emit("connected", false);
    });
    connection.onreconnected(() => {
      taskNotesHub.$emit("connected", true);
    });

    connection.on("UpdateNote", (task: TaskDto) => {
      taskStore.updateTask(task);
      taskNotesHub.$emit(`tasknote-updated-${task.type}`, task);
    });

    connection.on("SetDone", (task: TaskDto) => {
      taskStore.updateTask(task);
      taskNotesHub.$emit(`tasknote-done-${task.type}`, task);
    });

    connection.on("SetDeleted", (task: TaskDto) => {
      taskStore.updateTask(task);
      taskNotesHub.$emit(`tasknote-deleted-${task.type}`, task);
    });

    connection.on("WatchMyEzraReturn", (tasks: TaskDto[]) => {
      taskStore.setState(tasks);
    });
  }
};

export const encountersHub = {
  install(app: App) {
    const connection = new HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_APP_ENCOUNTERS_URL}/encounters`, {
        accessTokenFactory: async () => {
          const user = await (await getActiveUserManager())?.getUser();
          if (user && !user.expired) {
            return user.access_token;
          } else {
            return "";
          }
        }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Error)
      .build();

    let startedPromise: Promise<void> = Promise.resolve();
    function start() {
      if (connection.state == "Connected") {
        console.warn("Hub connection already established.");
        return Promise.resolve();
      }
      startedPromise = connection.start().catch(async () => {
        const user = await (await getActiveUserManager())?.getUser();
        if (!user || user.expired) {
          console.warn("Hub connection unavailable.");
          return Promise.resolve();
        }
        return new Promise((resolve, reject) =>
          setTimeout(() => start().then(resolve).catch(reject), 5000)
        );
      });
      return startedPromise;
    }

    const encountersEmitter = createEventEmitter<EncounterEvents>();
    const encounterStore = useEncounterStore();
    const hubApi = {
      ...encountersEmitter,
      start: async () => {
        await start();
        hubApi.$emit("connected", true);
      },

      connected: () => connection?.state === "Connected",

      stop: () => {
        if (hubApi.connected()) {
          connection.stop().catch(err => {
            console.error("Failed to disconnect with hub", err);
          });
        } else {
          console.warn(
            "Attempted to stop listening to hub, but was not connected."
          );
        }
        encounterStore.$reset();
      },

      watch: () => {
        if (!startedPromise) {
          console.error("Hub has no connection.");
          return Promise.reject();
        }
        return startedPromise
          .then(() => {
            const promise = connection.invoke("watch");
            return promise;
          })
          .then(val => encounterStore.setState(val))
          .catch(e => {
            console.error(e);
            return [];
          });
      },

      unwatch: () => {
        if (!startedPromise) {
          console.error("Hub has no connection.");
          return Promise.reject();
        }
        return startedPromise
          .then(() => {
            const promise = connection.invoke("unwatch");
            return promise;
          })
          .then(() => {
            encounterStore.$reset();
            return true;
          })
          .catch(e => {
            console.error(e);
            return false;
          });
      }
    };

    connection.onreconnecting(() => {
      hubApi.$emit("connected", false);
    });
    connection.onreconnected(() => {
      hubApi.$emit("connected", true);
    });

    installedEncountersHub = hubApi;
    app.config.globalProperties.$encountersHub = hubApi;

    connection.on("UpdateEncounter", (encounter: EncounterDto) => {
      encounterStore.updateEncounter(encounter);
    });
  }
};
